import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchGatewayConnection, processPayment } from "../../../api/api";
import { formatPaymentError } from "../../../utils/formatPaymentError";
import { handleCitPaymentApiResponse, applyCitDeclineOrErrorUi, CIT_OUTCOME } from "../../../utils/paymentOutcome";
import useResumePendingPayment from "../../../hooks/useResumePendingPayment";
import { useCheckout } from "../../../context/CheckoutContext";
import { validateBrazilianDocument } from "../../../utils/validationUtils";
import { looksTechnicalId } from "../../../utils/lineItemDisplay";
import { formatFrequencyUnit } from "../../../utils/commonUtils";
import PixPaymentModal from "../../modal/PixPaymentModal";
import { useTranslation } from "../../../hooks/useTranslation";
import { fetchSession, updateSession } from "../../../utils/paymentSession";
import PagbrasilPaymentFormView from "./PagbrasilPaymentFormView";
import useWebSocket from "../../../hooks/useWebSocket";
import { toast } from "react-toastify";

/**
 * Human-readable label for PagBrasil pix_rec_description (shown in the shopper's bank
 * PIX mandate screen). Falls back to the catalog price ID; the backend re-resolves
 * technical IDs against the billing platform catalog.
 */
const resolvePlanDisplayLabel = (planCatalogItem) => {
  const candidates = [
    planCatalogItem?.externalName,
    planCatalogItem?.planName,
    planCatalogItem?.description,
  ];
  const humanName = candidates.find(
    (v) => typeof v === "string" && v.trim() && !looksTechnicalId(v)
  );
  return humanName?.trim() || planCatalogItem?.id;
};

const PagbrasilPaymentForm = ({
  formData,
  itemDetails,
  billingPlatformId,
  routingEntries,
  planId,
  platformItemDetails,
  checkoutPageConfDto,
  checkoutProcessDto,

  // Props for Altruon variant
  onDocumentChange,
  onPaymentSuccess,
  isEmbeddedCheckout = false,
  onParentCallback,
  sessionId
}) => {

  const [documentNumber, setDocumentNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [documentError, setDocumentError] = useState("");
  const [pixResponse, setPixResponse] = useState(null);
  const [paymentSession, setPaymentSession] = useState(null);
  const [transactionId, setTransactionId] = useState(null);
  const { t } = useTranslation();
  const {
    triggerPaymentMethodEvent,
    resetTrigger,
    clientIp,
    paymentMethodName,
    platformName,
    isHostedPageMode,
  } = useCheckout();


  const pixPagBrasilRouting = useMemo(() => {
    return routingEntries?.find(
      (entry) => entry.paymentMethod === "pix" && entry.platform === "pagbrasil"
    );
  }, [routingEntries]);


  const handleDocumentChange = useCallback(
    (e) => {
      const value = e.target.value.toUpperCase();
      setDocumentNumber(value);
      setDocumentError("");
      onDocumentChange?.(value);
    },
    [onDocumentChange]
  );

  useEffect(() => {
    if (formData?.document && !documentNumber) {
      setDocumentNumber(String(formData.document).toUpperCase());
    }
  }, [formData?.document, documentNumber]);

  const buildPayload = (gatewayConnection, latestSession) => {
    const billingState = formData?.billingAddress?.state || latestSession?.customerData?.billingAddress?.state || "Brasília";
    const isBrazilStateValidated = !!billingState;

    // Check if the billingState is validated
    if (!isBrazilStateValidated) {
      setError(t('paymentForms.pix.selectStateError'));
      return;
    }

    return {
      paymentData: {
        gatewayConnectionId: gatewayConnection?.id,
        gatewayName: gatewayConnection?.platform,
        paymentMethod: "pix",
        // amount: itemDetails?.price || 1000,
        // currency: itemDetails?.currencyCode || "BRL",
        transactionId: "",
        additionalData: {
          payment_method_flow: "DIRECT",
          document: documentNumber,
          state: billingState,
        },
      },
      customerData: {
        firstName: formData?.firstName || latestSession?.customerData?.firstName,
        lastName: formData?.lastName || latestSession?.customerData?.lastName,
        email: formData?.email || latestSession?.customerData?.email,
        ip: clientIp || "192.0.126.111",
        billingAddress: {
          country: formData?.billingAddress?.country || latestSession?.customerData?.billingAddress?.country || "BR",
          zipCode: formData?.billingAddress?.zipCode || latestSession?.customerData?.billingAddress?.zipCode || "90210-000",
          street: formData?.billingAddress?.street || latestSession?.customerData?.billingAddress?.street || "",
          city: formData?.billingAddress?.city || latestSession?.customerData?.billingAddress?.city || "",
          state: formData?.billingAddress?.state || latestSession?.customerData?.billingAddress?.state || "",
          number: "1234",
        },
        shippingAddress: {},
      },
      billingData: {
        planId: platformItemDetails?.[0]?.id,
        billingPlatformId: billingPlatformId,
        billingPlatformName: itemDetails?.billingPlatformName || latestSession?.billingData?.billingPlatformName,
        frequency: latestSession?.billingData?.frequency || "",
        additionalData: {
          pix_rec_description: resolvePlanDisplayLabel(platformItemDetails?.[0]),
          pix_rec_cycle: latestSession?.billingData?.frequency || "",
        },
      },
    };
  };

  const handleProcessPayment = useCallback(async (payload) => {

    if (isEmbeddedCheckout) {
      onParentCallback?.({
        type: "finalizeFormData",
        data: { paymentMethod: "pix", gatewayName: "pagbrasil", documentNumber }
      });
      await new Promise((r) => setTimeout(r, 900));
    }

    if (!isEmbeddedCheckout) {
      await updateSession(sessionId, payload);
    }

    return processPayment(billingPlatformId, sessionId, payload);

  }, [billingPlatformId, documentNumber, isEmbeddedCheckout, onParentCallback, sessionId]);

  const handlePayNow = useCallback(async () => {
    const isBrazilNumberValidated = validateBrazilianDocument(documentNumber);
    if (!isBrazilNumberValidated) {
      setDocumentError(t('paymentForms.pix.invalidDocument'));
      return;
    } else {
      setDocumentError("");
    }
    if (!pixPagBrasilRouting) {
      setError(t("paymentForms.pix.unavailable"));
      return;
    }
    setLoading(true);
    try {
      const gateway_connection = await fetchGatewayConnection(pixPagBrasilRouting, t);
      const latestSession = await fetchSession(sessionId);
      // await new Promise(resolve => setTimeout(resolve, 800));      
      const payload = buildPayload(gateway_connection, latestSession);
      if (!payload) {
        setLoading(false);
        return;
      }
      const res = await handleProcessPayment(payload);
      const citUi = { isHostedPageMode, isEmbeddedCheckout, t, setError, onParentCallback, toast };
      const { handled } = handleCitPaymentApiResponse(res, citUi);
      if (handled) return;

      const redirectUrl = res?.data?.redirect_url && `${res.data.redirect_url}?trxId=${res.data.newTransactionId}`;

      if (res.status === 200) {
        if (isEmbeddedCheckout) {
          setTransactionId(res.data.newTransactionId || res.data.transactionId);
          setPixResponse(res);
          onPaymentSuccess?.(res, latestSession);
          if (redirectUrl) {
            const qrCodeUrl = res?.data?.qrCodeUrl;
            const isConsentOnly =
              res?.data?.pix_consent_only === true ||
              res?.data?.pix_consent_only === "true";
            onParentCallback?.({
              type: "actionRequired",
              action: isConsentOnly ? "PIX_CONSENT" : (qrCodeUrl ? "QR_CODE" : "REDIRECT"),
              url: redirectUrl,
              qrData: qrCodeUrl || undefined,
            });
          } else {
            onParentCallback?.({
              type: "success",
              transactionId: res.data.newTransactionId || res.data.transactionId,
            });
          }
        } else {
          setPaymentSession(latestSession);
          setPixResponse(res);
        }
      } else {
        if (isEmbeddedCheckout) {
          onParentCallback?.({ type: "failure", message: t("messages.error.somethingWrong") || "Transaction failed." });
        }
        throw new Error(t("messages.error.somethingWrong"));
      }
    } catch (e) {
      console.error("PIX Payment failed:", e);
      const citUi = { isHostedPageMode, isEmbeddedCheckout, t, setError, onParentCallback, toast };
      if (e?.response && handleCitPaymentApiResponse(e.response, citUi).handled) return;
      applyCitDeclineOrErrorUi(
        { outcome: CIT_OUTCOME.ERROR, message: formatPaymentError(e?.message, t) },
        citUi
      );
    } finally {
      setLoading(false);
    }
  }, [
    documentNumber,
    pixPagBrasilRouting,
    fetchGatewayConnection,
    handleProcessPayment,
    isEmbeddedCheckout,
    onParentCallback,
    t,
  ]);

  const handleResumePix = useCallback(
    async (paymentResponse, trxId) => {
      if (!sessionId) return;
      const latestSession = await fetchSession(sessionId);
      setPaymentSession(latestSession);
      setPixResponse({ data: paymentResponse });
      setTransactionId(trxId);
    },
    [sessionId]
  );

  useResumePendingPayment({
    paymentMethod: "pix",
    platform: "pagbrasil",
    consumed: isEmbeddedCheckout || pixResponse != null,
    onResumePix: handleResumePix,
  });


  useEffect(() => {
    if (
      triggerPaymentMethodEvent && (isEmbeddedCheckout || (paymentMethodName === "pix" && platformName === "pagbrasil"))) {
      handlePayNow();
      resetTrigger();
    }
  }, [
    triggerPaymentMethodEvent,
    paymentMethodName,
    platformName,
    handlePayNow,
    resetTrigger,
  ]);


  // Listen websocket only for SDK version
  useWebSocket(isEmbeddedCheckout && transactionId, (event) => {
        const data = JSON.parse(event.data);

        if (!data?.status) return;

        if (data.status === "success") {
            onParentCallback?.({
                type: "success",
                transactionId,
            });
        }

        if (data.status === "failed") {
            onParentCallback?.({
                type: "failure",
            });
        }
    },
      undefined,
      undefined,
      (err) => {
          console.error("CARD WS error", err);
      }
  );

  return (
    <PagbrasilPaymentFormView
      t={t}
      documentNumber={documentNumber}
      loading={loading}
      error={error}
      documentError={documentError}
      pixResponse={pixResponse}
      isEmbeddedCheckout={isEmbeddedCheckout}
      itemDetails={itemDetails}
      checkoutPageConfDto={checkoutPageConfDto}
      latestSession={paymentSession || checkoutProcessDto}
      onDocumentChange={handleDocumentChange}
    />
  );
};

export default PagbrasilPaymentForm;